import React from "react";
import { Input } from "@/components/ui/input";
import { TextField, TextareaField, NumberField, BoolField, ImageField, IconPickerField, ArrayEditor } from "@/components/cms/Fields";
import { RichTextField } from "@/components/cms/RichTextField";

const Group = ({ title, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">{title}</div>
    <div className="grid sm:grid-cols-2 gap-3">{children}</div>
  </div>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
  <label className="block">
    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</span>
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
      data-testid={testid}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </label>
);

const set = (d, k, v) => ({ ...d, [k]: v });
const setIn = (d, path, v) => {
  const c = { ...(d || {}) }; let o = c;
  for (let i = 0; i < path.length - 1; i++) { o[path[i]] = { ...(o[path[i]] || {}) }; o = o[path[i]]; }
  o[path[path.length - 1]] = v; return c;
};

// ---- Hero ----
const POSITION_OPTIONS = [
  { value: "top-left",     label: "Top-left" },
  { value: "top-right",    label: "Top-right" },
  { value: "bottom-left",  label: "Bottom-left" },
  { value: "bottom-right", label: "Bottom-right" },
];

const HeroForm = ({ data, onChange }) => {
  const cta = data.primary_cta || {};
  const wa = data.whatsapp_cta || {};
  const wd = data.watch_demo_cta || {};
  const ob = data.onboarding_cta || {};
  const watchDemo = data.watch_demo || {};
  const chatDemo = data.chat_demo || {};
  const lf = data.lead_form || {};
  const fields = lf.fields || {};
  return (
    <div className="space-y-4">
      {/* 1. HERO CONTENT */}
      <Group title="1. Hero content — eyebrow, headline, sub-headline">
        <TextField label="Eyebrow"                value={data.eyebrow}            onChange={(v) => onChange(set(data, "eyebrow", v))} />
        <TextField label="Headline · prefix"      value={data.headline_pre}       onChange={(v) => onChange(set(data, "headline_pre", v))} />
        <TextField label="Headline · highlighted" value={data.headline_highlight} onChange={(v) => onChange(set(data, "headline_highlight", v))} />
        <TextField label="Headline · suffix"      value={data.headline_post}      onChange={(v) => onChange(set(data, "headline_post", v))} />
        <div className="sm:col-span-2"><TextareaField label="Sub-headline"     value={data.subhead}          onChange={(v) => onChange(set(data, "subhead", v))} rows={2} /></div>
        <div className="sm:col-span-2"><TextareaField label="Supporting line"  value={data.supporting_line}  onChange={(v) => onChange(set(data, "supporting_line", v))} rows={2} /></div>
        <div className="sm:col-span-2">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Microcopy chips (under CTAs)</span>
          <ArrayEditor items={data.microcopy} onChange={(v) => onChange(set(data, "microcopy", v))} newItem={() => ""} label="Item" testid="hero-microcopy-arr"
            renderItem={(it, ch) => <Input value={it} onChange={(e) => ch(e.target.value)} placeholder="e.g. No credit card required" />} />
        </div>
      </Group>

      {/* 2. TRUST BADGES */}
      <Group title="2. Trust badges — primary row (white pills)">
        <div className="sm:col-span-2">
          <ArrayEditor items={data.trust_badges} onChange={(v) => onChange(set(data, "trust_badges", v))}
            newItem={() => ({ id: `tb-${Date.now().toString(36)}`, enabled: true, order: (data.trust_badges?.length || 0) + 1, icon: "ShieldCheck", label: "New badge" })}
            label="Badge" testid="hero-trust-badges-arr"
            renderItem={(it, ch) => (
              <div className="grid sm:grid-cols-[140px_1fr_120px] gap-2 items-end">
                <IconPickerField label="Icon"   value={it.icon}  onChange={(v) => ch({ ...it, icon: v })} />
                <TextField       label="Label"  value={it.label} onChange={(v) => ch({ ...it, label: v })} placeholder="e.g. Meta Compliant" />
                <BoolField       label="Enabled" value={!!it.enabled} onChange={(v) => ch({ ...it, enabled: v })} />
              </div>
            )} />
        </div>
      </Group>

      <Group title="2b. Trust badges — secondary row (green pills)">
        <div className="sm:col-span-2">
          <ArrayEditor items={data.trust_badges_secondary} onChange={(v) => onChange(set(data, "trust_badges_secondary", v))}
            newItem={() => ({ id: `tb2-${Date.now().toString(36)}`, enabled: true, order: (data.trust_badges_secondary?.length || 0) + 1, icon: "BadgeCheck", label: "New badge" })}
            label="Badge" testid="hero-trust-badges-secondary-arr"
            renderItem={(it, ch) => (
              <div className="grid sm:grid-cols-[140px_1fr_120px] gap-2 items-end">
                <IconPickerField label="Icon"   value={it.icon}  onChange={(v) => ch({ ...it, icon: v })} />
                <TextField       label="Label"  value={it.label} onChange={(v) => ch({ ...it, label: v })} placeholder="e.g. Dedicated Onboarding Support" />
                <BoolField       label="Enabled" value={!!it.enabled} onChange={(v) => ch({ ...it, enabled: v })} />
              </div>
            )} />
        </div>
      </Group>

      {/* 3. CTA BUTTONS */}
      <Group title="3. CTA buttons">
        <div className="sm:col-span-2 grid sm:grid-cols-[1fr_1fr_110px] gap-2 items-end rounded-lg border border-slate-200 p-3 bg-slate-50/50">
          <TextField label="Primary CTA · label" value={cta.label} onChange={(v) => onChange(setIn(data, ["primary_cta", "label"], v))} />
          <TextField label="Primary CTA · URL"   value={cta.href}  onChange={(v) => onChange(setIn(data, ["primary_cta", "href"], v))} />
          <BoolField label="Enabled" value={cta.enabled !== false} onChange={(v) => onChange(setIn(data, ["primary_cta", "enabled"], v))} />
        </div>
        <div className="sm:col-span-2 grid sm:grid-cols-[1fr_1fr_110px] gap-2 items-end rounded-lg border border-slate-200 p-3 bg-slate-50/50">
          <TextField label="WhatsApp CTA · label" value={wa.label} onChange={(v) => onChange(setIn(data, ["whatsapp_cta", "label"], v))} />
          <TextField label="WhatsApp CTA · URL"   value={wa.href}  onChange={(v) => onChange(setIn(data, ["whatsapp_cta", "href"], v))} placeholder="https://wa.me/91…" />
          <BoolField label="Enabled" value={wa.enabled !== false} onChange={(v) => onChange(setIn(data, ["whatsapp_cta", "enabled"], v))} />
        </div>
        <div className="sm:col-span-2 grid sm:grid-cols-[1fr_110px] gap-2 items-end rounded-lg border border-slate-200 p-3 bg-slate-50/50">
          <TextField label="Watch Demo CTA · label" value={wd.label} onChange={(v) => onChange(setIn(data, ["watch_demo_cta", "label"], v))} />
          <BoolField label="Enabled" value={wd.enabled !== false} onChange={(v) => onChange(setIn(data, ["watch_demo_cta", "enabled"], v))} />
        </div>
        <div className="sm:col-span-2 grid sm:grid-cols-[1fr_1fr_110px] gap-2 items-end rounded-lg border border-slate-200 p-3 bg-slate-50/50">
          <TextField label="Onboarding link · label" value={ob.label} onChange={(v) => onChange(setIn(data, ["onboarding_cta", "label"], v))} />
          <TextField label="Onboarding link · URL"   value={ob.href}  onChange={(v) => onChange(setIn(data, ["onboarding_cta", "href"], v))} placeholder="/whatsapp-onboarding" />
          <BoolField label="Enabled" value={ob.enabled !== false} onChange={(v) => onChange(setIn(data, ["onboarding_cta", "enabled"], v))} />
        </div>
      </Group>

      {/* 4. WATCH DEMO VIDEO */}
      <Group title="4. Watch Demo video (modal popup)">
        <TextField label="YouTube URL (paste a normal video link)" value={watchDemo.youtube_url} onChange={(v) => onChange(setIn(data, ["watch_demo", "youtube_url"], v))} placeholder="https://www.youtube.com/watch?v=…" />
        <TextField label="Embed URL (optional · overrides YouTube URL)" value={watchDemo.embed_url} onChange={(v) => onChange(setIn(data, ["watch_demo", "embed_url"], v))} placeholder="https://www.youtube.com/embed/…" />
        <BoolField label="Autoplay" value={watchDemo.autoplay !== false} onChange={(v) => onChange(setIn(data, ["watch_demo", "autoplay"], v))} />
        <BoolField label="Enabled" value={watchDemo.enabled !== false} onChange={(v) => onChange(setIn(data, ["watch_demo", "enabled"], v))} />
      </Group>

      {/* 5. CHAT DEMO ANIMATION */}
      <Group title="5. Chat demo animation (right column)">
        <BoolField label="Enabled" value={chatDemo.enabled !== false} onChange={(v) => onChange(setIn(data, ["chat_demo", "enabled"], v))} />
        <TextField label="Assistant name"    value={chatDemo.assistant_name}     onChange={(v) => onChange(setIn(data, ["chat_demo", "assistant_name"], v))} />
        <TextField label="Status text"       value={chatDemo.status_text}        onChange={(v) => onChange(setIn(data, ["chat_demo", "status_text"], v))} placeholder="online" />
        <TextField label="Input placeholder" value={chatDemo.input_placeholder}  onChange={(v) => onChange(setIn(data, ["chat_demo", "input_placeholder"], v))} placeholder="Type a message…" />
        <div className="sm:col-span-2">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Conversation script</span>
          <p className="text-[11px] text-slate-500 mt-1">Each line plays in sequence. Use <code>in</code> for the customer and <code>out</code> for Wappit.</p>
          <ArrayEditor items={chatDemo.conversation || []} onChange={(v) => onChange(setIn(data, ["chat_demo", "conversation"], v))}
            newItem={() => ({ side: "out", text: "", delay_ms: 700 })}
            label="Bubble" testid="hero-chat-conv-arr"
            renderItem={(m, ch) => (
              <div className="grid sm:grid-cols-[120px_1fr_120px] gap-2 items-end">
                <SelectField label="Side" value={m.side} onChange={(v) => ch({ ...m, side: v })} options={[{ value: "in", label: "Customer (in)" }, { value: "out", label: "Wappit (out)" }]} />
                <TextField   label="Message text" value={m.text} onChange={(v) => ch({ ...m, text: v })} />
                <NumberField label="Typing delay (ms)" value={m.delay_ms ?? 700} onChange={(v) => ch({ ...m, delay_ms: v })} />
              </div>
            )} />
        </div>
      </Group>

      {/* 6. FLOATING STATS */}
      <Group title="6. Floating stat cards (overlay on chat demo)">
        <div className="sm:col-span-2">
          <ArrayEditor items={data.floating_stats} onChange={(v) => onChange(set(data, "floating_stats", v))}
            newItem={() => ({ id: `fs-${Date.now().toString(36)}`, enabled: true, position: "bottom-right", label: "Label", value: "100%", delta: "" })}
            label="Stat card" testid="hero-floating-stats-arr"
            renderItem={(it, ch) => (
              <div className="grid sm:grid-cols-[160px_1fr_1fr_140px_110px] gap-2 items-end">
                <SelectField label="Position" value={it.position} onChange={(v) => ch({ ...it, position: v })} options={POSITION_OPTIONS} />
                <TextField   label="Label"    value={it.label}    onChange={(v) => ch({ ...it, label: v })} />
                <TextField   label="Value"    value={it.value}    onChange={(v) => ch({ ...it, value: v })} />
                <TextField   label="Delta"    value={it.delta}    onChange={(v) => ch({ ...it, delta: v })} placeholder="↑ 4.2x" />
                <BoolField   label="Enabled"  value={!!it.enabled} onChange={(v) => ch({ ...it, enabled: v })} />
              </div>
            )} />
        </div>
      </Group>

      {/* 7. LEAD CAPTURE FORM */}
      <Group title="7. Hero Lead Capture Form">
        <BoolField label="Form enabled" value={lf.enabled !== false} onChange={(v) => onChange(setIn(data, ["lead_form", "enabled"], v))} />
        <TextField label="Submit button label" value={lf.submit_label} onChange={(v) => onChange(setIn(data, ["lead_form", "submit_label"], v))} />

        {["name", "phone", "company", "volume"].map((k) => (
          <div key={k} className="sm:col-span-2 grid sm:grid-cols-2 gap-2 rounded-lg border border-slate-200 p-3 bg-slate-50/50">
            <TextField label={`${k.toUpperCase()} · label`}        value={(fields[k] || {}).label}       onChange={(v) => onChange(setIn(data, ["lead_form", "fields", k, "label"], v))} />
            <TextField label={`${k.toUpperCase()} · placeholder`}  value={(fields[k] || {}).placeholder} onChange={(v) => onChange(setIn(data, ["lead_form", "fields", k, "placeholder"], v))} />
          </div>
        ))}

        <div className="sm:col-span-2">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Monthly volume — dropdown options</span>
          <ArrayEditor items={lf.volume_options || []} onChange={(v) => onChange(setIn(data, ["lead_form", "volume_options"], v))}
            newItem={() => ""} label="Option" testid="hero-form-volume-options-arr"
            renderItem={(it, ch) => <Input value={it} onChange={(e) => ch(e.target.value)} placeholder="e.g. 10,000 – 50,000" />} />
        </div>

        <div className="sm:col-span-2"><TextareaField label="Disclaimer (under form)" value={lf.disclaimer} onChange={(v) => onChange(setIn(data, ["lead_form", "disclaimer"], v))} rows={2} /></div>

        <TextField label="Calendly URL"               value={lf.calendly_url} onChange={(v) => onChange(setIn(data, ["lead_form", "calendly_url"], v))} />
        <BoolField label="Auto-open Calendly on verify" value={lf.auto_open_calendly_on_verify !== false} onChange={(v) => onChange(setIn(data, ["lead_form", "auto_open_calendly_on_verify"], v))} />

        <TextField label="Success title (use {first_name} for name)" value={lf.success_title} onChange={(v) => onChange(setIn(data, ["lead_form", "success_title"], v))} />
        <TextField label="Toast on verify"                            value={lf.toast_verified} onChange={(v) => onChange(setIn(data, ["lead_form", "toast_verified"], v))} />
        <div className="sm:col-span-2"><TextareaField label="Success message — when WhatsApp number IS verified" value={lf.success_message_verified} onChange={(v) => onChange(setIn(data, ["lead_form", "success_message_verified"], v))} rows={2} /></div>
        <div className="sm:col-span-2"><TextareaField label="Success message — when verification is skipped"   value={lf.success_message_unverified} onChange={(v) => onChange(setIn(data, ["lead_form", "success_message_unverified"], v))} rows={2} /></div>
        <TextField label="Error · OTP send failed"  value={lf.error_otp_failed}        onChange={(v) => onChange(setIn(data, ["lead_form", "error_otp_failed"], v))} />
        <TextField label="Error · submission failed" value={lf.error_submission_failed} onChange={(v) => onChange(setIn(data, ["lead_form", "error_submission_failed"], v))} />
      </Group>
    </div>
  );
};

// (Input imported at top of file)

// ---- Trust Stats ----
const TrustStatsForm = ({ data, onChange }) => (
  <div className="space-y-4">
    <Group title="Animated KPI counters">
      <div className="sm:col-span-2">
        <ArrayEditor items={data.stats} onChange={(v) => onChange(set(data, "stats", v))}
          newItem={{ value: 0, suffix: "+", label: "Label" }} label="Stat" testid="stats-arr"
          renderItem={(it, ch) => (
            <div className="grid sm:grid-cols-3 gap-2">
              <NumberField label="Value" value={it.value} onChange={(v) => ch({ ...it, value: v })} />
              <TextField label="Suffix" value={it.suffix} onChange={(v) => ch({ ...it, suffix: v })} />
              <TextField label="Label" value={it.label} onChange={(v) => ch({ ...it, label: v })} />
            </div>
          )} />
      </div>
    </Group>
    <Group title="Capability badges (chips)">
      <div className="sm:col-span-2">
        <ArrayEditor items={data.badges} onChange={(v) => onChange(set(data, "badges", v))} newItem={() => ""} label="Badge" testid="badges-arr"
          renderItem={(it, ch) => <Input value={it} onChange={(e) => ch(e.target.value)} placeholder="e.g. Multi-Agent Team Inbox" />} />
      </div>
    </Group>
  </div>
);

// ---- Pricing ----
const PricingForm = ({ data, onChange }) => (
  <div className="space-y-4">
    <Group title="Section text">
      <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => onChange(set(data, "eyebrow", v))} />
      <TextField label="Heading" value={data.heading} onChange={(v) => onChange(set(data, "heading", v))} />
      <div className="sm:col-span-2"><TextareaField label="Sub-headline" value={data.subhead} onChange={(v) => onChange(set(data, "subhead", v))} rows={2} /></div>
    </Group>
    <Group title="Plans">
      <div className="sm:col-span-2">
        <ArrayEditor items={data.plans} onChange={(v) => onChange(set(data, "plans", v))}
          newItem={{ name: "New", billed: "Billed", price: "₹0", suffix: "/month", note: "+ GST", total: null, popular: false }}
          label="Plan" testid="plans-arr"
          renderItem={(it, ch) => (
            <div className="grid sm:grid-cols-3 gap-2">
              <TextField label="Name" value={it.name} onChange={(v) => ch({ ...it, name: v })} />
              <TextField label="Billed label" value={it.billed} onChange={(v) => ch({ ...it, billed: v })} />
              <TextField label="Price" value={it.price} onChange={(v) => ch({ ...it, price: v })} />
              <TextField label="Suffix" value={it.suffix} onChange={(v) => ch({ ...it, suffix: v })} />
              <TextField label="Note" value={it.note} onChange={(v) => ch({ ...it, note: v })} />
              <TextField label="Total billing line" value={it.total || ""} onChange={(v) => ch({ ...it, total: v || null })} />
              <div className="sm:col-span-3"><BoolField label="Most popular plan" value={it.popular} onChange={(v) => ch({ ...it, popular: v })} /></div>
            </div>
          )} />
      </div>
    </Group>
    <Group title="Features (shown on every plan)">
      <div className="sm:col-span-2">
        <ArrayEditor items={data.features} onChange={(v) => onChange(set(data, "features", v))} newItem={() => ""} label="Feature" testid="features-arr"
          renderItem={(it, ch) => <Input value={it} onChange={(e) => ch(e.target.value)} />} />
      </div>
    </Group>
  </div>
);

// ---- Testimonials ----
const TestimonialsForm = ({ data, onChange }) => (
  <div className="space-y-4">
    <Group title="Section header">
      <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => onChange(set(data, "eyebrow", v))} />
      <TextField label="Heading" value={data.heading} onChange={(v) => onChange(set(data, "heading", v))} />
      <div className="sm:col-span-2">
        <TextareaField label="Subheading" value={data.subheading} onChange={(v) => onChange(set(data, "subheading", v))} rows={2} />
      </div>
    </Group>

    <Group title="Customer testimonials">
      <div className="sm:col-span-2">
        <ArrayEditor items={data.items} onChange={(v) => onChange(set(data, "items", v))}
          newItem={{ name: "", designation: "", company: "", industry: "", role: "", quote: "", stat: "", image: "", company_logo: "", rating: 5, featured: false, order: 0, enabled: true }}
          label="Testimonial" testid="testimonials-arr"
          renderItem={(it, ch) => (
            <div className="grid sm:grid-cols-2 gap-2">
              <TextField label="Client name" value={it.name} onChange={(v) => ch({ ...it, name: v })} />
              <TextField label="Designation" value={it.designation} onChange={(v) => ch({ ...it, designation: v })} />
              <TextField label="Company" value={it.company} onChange={(v) => ch({ ...it, company: v })} />
              <TextField label="Industry" value={it.industry} onChange={(v) => ch({ ...it, industry: v })} />
              <div className="sm:col-span-2"><TextareaField label="Quote / review" value={it.quote} onChange={(v) => ch({ ...it, quote: v })} rows={3} /></div>
              <TextField label="Highlight metric (e.g. 3× Faster Site Visits)" value={it.stat} onChange={(v) => ch({ ...it, stat: v })} />
              <NumberField label="Rating (1–5)" value={it.rating ?? 5} onChange={(v) => ch({ ...it, rating: Math.max(1, Math.min(5, Number(v) || 5)) })} />
              <div className="sm:col-span-2"><ImageField label="Client photo" value={it.image} onChange={(v) => ch({ ...it, image: v })} /></div>
              <div className="sm:col-span-2"><ImageField label="Company logo" value={it.company_logo} onChange={(v) => ch({ ...it, company_logo: v })} /></div>
              <NumberField label="Display order" value={it.order ?? 0} onChange={(v) => ch({ ...it, order: Number(v) || 0 })} />
              <BoolField label="Featured (large card)" value={!!it.featured} onChange={(v) => ch({ ...it, featured: v })} />
              <BoolField label="Enabled (show on site)" value={it.enabled !== false} onChange={(v) => ch({ ...it, enabled: v })} />
            </div>
          )} />
      </div>
    </Group>

    <Group title="KPI cards (4 metrics)">
      <div className="sm:col-span-2">
        <ArrayEditor items={data.kpis} onChange={(v) => onChange(set(data, "kpis", v))}
          newItem={{ value: "", label: "", sublabel: "", gradient: "from-emerald-500 to-teal-500" }}
          label="KPI" testid="testimonials-kpis"
          renderItem={(it, ch) => (
            <div className="grid sm:grid-cols-2 gap-2">
              <TextField label="Value (e.g. 45K+, 98%, 3×, 24×7)" value={it.value} onChange={(v) => ch({ ...it, value: v })} />
              <TextField label="Label" value={it.label} onChange={(v) => ch({ ...it, label: v })} />
              <TextField label="Sublabel (e.g. Last 30 days, Average)" value={it.sublabel} onChange={(v) => ch({ ...it, sublabel: v })} />
              <div className="sm:col-span-2">
                <SelectField label="Gradient" value={it.gradient} onChange={(v) => ch({ ...it, gradient: v })}
                  options={[
                    { value: "from-emerald-500 to-teal-500",  label: "Emerald → Teal" },
                    { value: "from-blue-500 to-cyan-500",     label: "Blue → Cyan" },
                    { value: "from-violet-500 to-fuchsia-500", label: "Violet → Fuchsia" },
                    { value: "from-amber-500 to-orange-500",  label: "Amber → Orange" },
                    { value: "from-rose-500 to-pink-500",     label: "Rose → Pink" },
                  ]} />
              </div>
            </div>
          )} />
      </div>
    </Group>

    <Group title="Before → After">
      <TextField label='Before title (e.g. "Before Wappit")' value={data.before_after?.before_title || ""} onChange={(v) => onChange(setIn(data, "before_after.before_title", v))} />
      <TextField label='After title (e.g. "After Wappit")' value={data.before_after?.after_title || ""} onChange={(v) => onChange(setIn(data, "before_after.after_title", v))} />
      <div className="sm:col-span-2">
        <ArrayEditor items={data.before_after?.before || []} onChange={(v) => onChange(setIn(data, "before_after.before", v))}
          newItem={""} label="Before bullet" testid="ba-before"
          renderItem={(it, ch) => (
            <TextField label="Bullet" value={it} onChange={ch} />
          )} />
      </div>
      <div className="sm:col-span-2">
        <ArrayEditor items={data.before_after?.after || []} onChange={(v) => onChange(setIn(data, "before_after.after", v))}
          newItem={""} label="After bullet" testid="ba-after"
          renderItem={(it, ch) => (
            <TextField label="Bullet" value={it} onChange={ch} />
          )} />
      </div>
    </Group>

    <Group title="Industries">
      <div className="sm:col-span-2">
        <ArrayEditor items={data.industries} onChange={(v) => onChange(set(data, "industries", v))}
          newItem={{ name: "", icon: "Building2" }} label="Industry" testid="testimonials-industries"
          renderItem={(it, ch) => (
            <div className="grid sm:grid-cols-2 gap-2">
              <TextField label="Name" value={it.name} onChange={(v) => ch({ ...it, name: v })} />
              <IconPickerField label="Icon" value={it.icon} onChange={(v) => ch({ ...it, icon: v })} />
            </div>
          )} />
      </div>
    </Group>

    <Group title="Client logos (shown in the Trust strip)">
      <div className="sm:col-span-2">
        <ArrayEditor items={data.client_logos} onChange={(v) => onChange(set(data, "client_logos", v))}
          newItem={{ name: "", logo_url: "", website: "" }} label="Client" testid="testimonials-client-logos"
          renderItem={(it, ch) => (
            <div className="grid sm:grid-cols-2 gap-2">
              <TextField label="Company name" value={it.name} onChange={(v) => ch({ ...it, name: v })} />
              <TextField label="Website URL (optional)" value={it.website} onChange={(v) => ch({ ...it, website: v })} />
              <div className="sm:col-span-2"><ImageField label="Logo (SVG / PNG preferred)" value={it.logo_url} onChange={(v) => ch({ ...it, logo_url: v })} /></div>
            </div>
          )} />
      </div>
    </Group>
  </div>
);

// ---- FAQ ----
const FAQForm = ({ data, onChange }) => (
  <Group title="Questions & answers">
    <div className="sm:col-span-2">
      <ArrayEditor items={data.items} onChange={(v) => onChange(set(data, "items", v))}
        newItem={{ q: "", a: "" }} label="Q&A" testid="faq-arr"
        renderItem={(it, ch) => (
          <div className="space-y-2">
            <TextField label="Question" value={it.q} onChange={(v) => ch({ ...it, q: v })} />
            <TextareaField label="Answer" value={it.a} onChange={(v) => ch({ ...it, a: v })} rows={3} />
          </div>
        )} />
    </div>
  </Group>
);

// ---- Industries & Features (icon picker) ----
const IconList = (label, itemKey, hint) => ({ data, onChange }) => (
  <Group title={label}>
    <div className="sm:col-span-2">
      <ArrayEditor items={data.items} onChange={(v) => onChange(set(data, "items", v))}
        newItem={{ name: "", icon: "Sparkles" }} label={itemKey} testid={`${itemKey.toLowerCase()}-arr`}
        renderItem={(it, ch) => (
          <div className="grid sm:grid-cols-2 gap-2">
            <TextField label="Name" value={it.name} onChange={(v) => ch({ ...it, name: v })} />
            <IconPickerField label="Icon" value={it.icon} onChange={(v) => ch({ ...it, icon: v })} />
            {it.desc !== undefined && (
              <div className="sm:col-span-2"><TextareaField label="Description" value={it.desc} onChange={(v) => ch({ ...it, desc: v })} rows={2} /></div>
            )}
          </div>
        )} />
      <p className="text-[11px] text-slate-500 mt-2">{hint}</p>
    </div>
  </Group>
);

const FeaturesForm = ({ data, onChange }) => (
  <Group title="Feature cards">
    <div className="sm:col-span-2">
      <ArrayEditor items={data.items} onChange={(v) => onChange(set(data, "items", v))}
        newItem={{ title: "", desc: "", icon: "Sparkles" }} label="Feature" testid="features-form-arr"
        renderItem={(it, ch) => (
          <div className="grid sm:grid-cols-2 gap-2">
            <TextField label="Title" value={it.title} onChange={(v) => ch({ ...it, title: v })} />
            <IconPickerField label="Icon" value={it.icon} onChange={(v) => ch({ ...it, icon: v })} />
            <div className="sm:col-span-2"><TextareaField label="Description" value={it.desc} onChange={(v) => ch({ ...it, desc: v })} rows={2} /></div>
          </div>
        )} />
    </div>
  </Group>
);

const IndustriesForm = ({ data, onChange }) => (
  <Group title="Industries">
    <div className="sm:col-span-2">
      <ArrayEditor items={data.items} onChange={(v) => onChange(set(data, "items", v))}
        newItem={{ name: "", icon: "Sparkles" }} label="Industry" testid="industries-form-arr"
        renderItem={(it, ch) => (
          <div className="grid sm:grid-cols-2 gap-2">
            <TextField label="Name" value={it.name} onChange={(v) => ch({ ...it, name: v })} />
            <IconPickerField label="Icon" value={it.icon} onChange={(v) => ch({ ...it, icon: v })} />
          </div>
        )} />
    </div>
  </Group>
);

// ---- Navigation ----
const NavigationForm = ({ data, onChange }) => (
  <div className="space-y-4">
    <Group title="Nav links">
      <div className="sm:col-span-2">
        <ArrayEditor items={data.links} onChange={(v) => onChange(set(data, "links", v))}
          newItem={{ label: "", href: "#" }} label="Link" testid="nav-links-arr"
          renderItem={(it, ch) => (
            <div className="grid sm:grid-cols-2 gap-2">
              <TextField label="Label" value={it.label} onChange={(v) => ch({ ...it, label: v })} />
              <TextField label="URL / anchor" value={it.href} onChange={(v) => ch({ ...it, href: v })} />
            </div>
          )} />
      </div>
    </Group>
    <Group title="Login button">
      <TextField label="Label" value={data.login?.label} onChange={(v) => onChange(setIn(data, ["login", "label"], v))} />
      <TextField label="URL" value={data.login?.href} onChange={(v) => onChange(setIn(data, ["login", "href"], v))} />
    </Group>
    <Group title="Book Demo button">
      <TextField label="Label" value={data.book_demo?.label} onChange={(v) => onChange(setIn(data, ["book_demo", "label"], v))} />
      <TextField label="URL" value={data.book_demo?.href} onChange={(v) => onChange(setIn(data, ["book_demo", "href"], v))} />
    </Group>
  </div>
);

// ---- Footer ----
const FooterForm = ({ data, onChange }) => (
  <div className="space-y-4">
    <Group title="Brand / tagline">
      <div className="sm:col-span-2"><TextareaField label="Tagline" value={data.tagline} onChange={(v) => onChange(set(data, "tagline", v))} rows={2} /></div>
      <div className="sm:col-span-2"><TextareaField label="Trust statement" value={data.trust_statement} onChange={(v) => onChange(set(data, "trust_statement", v))} rows={2} /></div>
    </Group>
    <Group title="Contact details">
      <TextField label="Phone (display)" value={data.phone_display} onChange={(v) => onChange(set(data, "phone_display", v))} />
      <TextField label="Phone (E.164)" value={data.phone_e164} onChange={(v) => onChange(set(data, "phone_e164", v))} />
      <TextField label="WhatsApp link" value={data.wa_link} onChange={(v) => onChange(set(data, "wa_link", v))} />
      <TextField label="Email" value={data.email} onChange={(v) => onChange(set(data, "email", v))} />
      <TextField label="Hours" value={data.hours} onChange={(v) => onChange(set(data, "hours", v))} />
    </Group>
    <Group title="Office address">
      <TextField label="Line 1" value={data.address?.line1} onChange={(v) => onChange(setIn(data, ["address", "line1"], v))} />
      <TextField label="Line 2" value={data.address?.line2} onChange={(v) => onChange(setIn(data, ["address", "line2"], v))} />
      <TextField label="Line 3" value={data.address?.line3} onChange={(v) => onChange(setIn(data, ["address", "line3"], v))} />
    </Group>
    <Group title="Social links">
      <div className="sm:col-span-2">
        <ArrayEditor items={data.social} onChange={(v) => onChange(set(data, "social", v))}
          newItem={{ platform: "", url: "#" }} label="Social" testid="social-arr"
          renderItem={(it, ch) => (
            <div className="grid sm:grid-cols-2 gap-2">
              <TextField label="Platform (x · linkedin · youtube · instagram · github)" value={it.platform} onChange={(v) => ch({ ...it, platform: v })} />
              <TextField label="URL" value={it.url} onChange={(v) => ch({ ...it, url: v })} />
            </div>
          )} />
      </div>
    </Group>
  </div>
);

// ---- SEO ----
const SEOForm = ({ data, onChange }) => (
  <div className="space-y-4">
    <Group title="Page meta">
      <TextField label="Site title" value={data.site_title} onChange={(v) => onChange(set(data, "site_title", v))} />
      <TextField label="Canonical URL" value={data.canonical} onChange={(v) => onChange(set(data, "canonical", v))} />
      <div className="sm:col-span-2"><TextareaField label="Meta description" value={data.meta_description} onChange={(v) => onChange(set(data, "meta_description", v))} rows={3} /></div>
    </Group>
    <Group title="Keywords">
      <div className="sm:col-span-2">
        <ArrayEditor items={data.keywords} onChange={(v) => onChange(set(data, "keywords", v))}
          newItem={() => ""} label="Keyword" testid="keywords-arr"
          renderItem={(it, ch) => <Input value={it} onChange={(e) => ch(e.target.value)} placeholder="e.g. WhatsApp Business API" />} />
      </div>
    </Group>
    <Group title="Brand assets">
      <div className="sm:col-span-2"><ImageField label="Favicon" value={data.favicon} onChange={(v) => onChange(set(data, "favicon", v))} /></div>
      <div className="sm:col-span-2"><ImageField label="Logo" value={data.logo} onChange={(v) => onChange(set(data, "logo", v))} /></div>
      <div className="sm:col-span-2"><ImageField label="Open Graph image (1200×630)" value={data.og_image} onChange={(v) => onChange(set(data, "og_image", v))} /></div>
    </Group>
  </div>
);

const IndustriesIcons = IconList("Industries", "Industry", "Pick a lucide-react icon name to render at the top of each card.");
const FeaturesIcons = IconList("Features", "Feature", "Pick a lucide-react icon name to render at the top of each card.");

// ---- Global Site Settings ----
const GlobalSettingsForm = ({ data, onChange }) => (
  <div className="space-y-4">
    <Group title="Brand">
      <TextField label="Website name" value={data.website_name} onChange={(v) => onChange(set(data, "website_name", v))} />
      <TextField label="Company name" value={data.company_name} onChange={(v) => onChange(set(data, "company_name", v))} />
      <div className="sm:col-span-2"><ImageField label="Company logo" value={data.company_logo} onChange={(v) => onChange(set(data, "company_logo", v))} /></div>
      <div className="sm:col-span-2"><ImageField label="Favicon" value={data.favicon} onChange={(v) => onChange(set(data, "favicon", v))} /></div>
    </Group>
    <Group title="Default SEO">
      <TextField label="Default SEO title" value={data.default_seo_title} onChange={(v) => onChange(set(data, "default_seo_title", v))} />
      <div className="sm:col-span-2"><TextareaField label="Default meta description" value={data.default_meta_description} onChange={(v) => onChange(set(data, "default_meta_description", v))} rows={2} /></div>
    </Group>
    <Group title="Contact">
      <TextField label="Contact email" value={data.contact_email} onChange={(v) => onChange(set(data, "contact_email", v))} />
      <TextField label="Support email" value={data.support_email} onChange={(v) => onChange(set(data, "support_email", v))} />
      <TextField label="Phone number" value={data.phone_number} onChange={(v) => onChange(set(data, "phone_number", v))} />
      <TextField label="WhatsApp number (E.164)" value={data.whatsapp_number} onChange={(v) => onChange(set(data, "whatsapp_number", v))} />
      <div className="sm:col-span-2"><TextareaField label="Address" value={data.address} onChange={(v) => onChange(set(data, "address", v))} rows={2} /></div>
      <TextField label="Business hours" value={data.business_hours} onChange={(v) => onChange(set(data, "business_hours", v))} />
    </Group>
    <Group title="Social links">
      <div className="sm:col-span-2">
        <ArrayEditor items={data.social} onChange={(v) => onChange(set(data, "social", v))}
          newItem={{ platform: "", url: "" }} label="Social" testid="gs-social-arr"
          renderItem={(it, ch) => (
            <div className="grid sm:grid-cols-2 gap-2">
              <TextField label="Platform" value={it.platform} onChange={(v) => ch({ ...it, platform: v })} />
              <TextField label="URL" value={it.url} onChange={(v) => ch({ ...it, url: v })} />
            </div>
          )} />
      </div>
    </Group>
    <Group title="Analytics & tracking IDs">
      <TextField label="Google Analytics ID (G-XXXX)" value={data.analytics?.google_analytics_id} onChange={(v) => onChange(setIn(data, ["analytics", "google_analytics_id"], v))} />
      <TextField label="Google Tag Manager ID (GTM-XXXX)" value={data.analytics?.google_tag_manager_id} onChange={(v) => onChange(setIn(data, ["analytics", "google_tag_manager_id"], v))} />
      <TextField label="Meta Pixel ID" value={data.analytics?.meta_pixel_id} onChange={(v) => onChange(setIn(data, ["analytics", "meta_pixel_id"], v))} />
      <TextField label="Microsoft Clarity ID" value={data.analytics?.microsoft_clarity_id} onChange={(v) => onChange(setIn(data, ["analytics", "microsoft_clarity_id"], v))} />
    </Group>
  </div>
);

// ---- Flow Builder (Chatbot section) ----
const FLOW_TONES = [
  { value: "whatsapp", label: "WhatsApp (emerald)" },
  { value: "ai",       label: "AI (violet)" },
  { value: "cond",     label: "Routing (orange)" },
  { value: "sales",    label: "Sales (pink)" },
  { value: "support",  label: "Support (blue)" },
  { value: "crm",      label: "CRM (sky)" },
  { value: "reply",    label: "Reply (teal)" },
  { value: "end",      label: "End (gradient emerald)" },
];
const FLOW_BRANCHES = [
  { value: "C", label: "Centered (full width)" },
  { value: "L", label: "Branch · Left half" },
  { value: "R", label: "Branch · Right half" },
];
const CAP_TONES = [
  { value: "violet",  label: "Violet" },
  { value: "amber",   label: "Amber" },
  { value: "blue",    label: "Blue" },
  { value: "emerald", label: "Emerald" },
  { value: "rose",    label: "Rose" },
  { value: "sky",     label: "Sky" },
];

const FlowBuilderForm = ({ data, onChange }) => (
  <div className="space-y-4">
    <Group title="Headline & description">
      <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => onChange(set(data, "eyebrow", v))} />
      <TextField label="Heading · prefix" value={data.heading_pre} onChange={(v) => onChange(set(data, "heading_pre", v))} />
      <TextField label="Heading · highlighted (gradient)" value={data.heading_highlight} onChange={(v) => onChange(set(data, "heading_highlight", v))} />
      <TextField label="Console title (inside the canvas)" value={data.console_title} onChange={(v) => onChange(set(data, "console_title", v))} />
      <div className="sm:col-span-2"><TextareaField label="Description" value={data.description} onChange={(v) => onChange(set(data, "description", v))} rows={3} /></div>
      <div className="sm:col-span-2"><TextareaField label="Trust line (below capabilities)" value={data.trust_line} onChange={(v) => onChange(set(data, "trust_line", v))} rows={2} /></div>
    </Group>

    <Group title="Feature checklist">
      <div className="sm:col-span-2">
        <ArrayEditor items={data.feature_list} onChange={(v) => onChange(set(data, "feature_list", v))}
          newItem={() => ""} label="Feature" testid="flow-feature-arr"
          renderItem={(it, ch) => <Input value={it} onChange={(e) => ch(e.target.value)} placeholder="e.g. Visual drag & drop builder" />} />
      </div>
    </Group>

    <Group title="Capability pills (4 cards)">
      <div className="sm:col-span-2">
        <ArrayEditor items={data.capabilities} onChange={(v) => onChange(set(data, "capabilities", v))}
          newItem={{ icon: "Bot", title: "New capability", sub: "Short subtitle", tone: "emerald" }} label="Capability" testid="flow-cap-arr"
          renderItem={(it, ch) => (
            <div className="grid sm:grid-cols-2 gap-2">
              <IconPickerField label="Icon" value={it.icon} onChange={(v) => ch({ ...it, icon: v })} />
              <SelectField label="Tone" value={it.tone} onChange={(v) => ch({ ...it, tone: v })} options={CAP_TONES} />
              <TextField label="Title" value={it.title} onChange={(v) => ch({ ...it, title: v })} />
              <TextField label="Subtitle" value={it.sub} onChange={(v) => ch({ ...it, sub: v })} />
            </div>
          )} />
      </div>
    </Group>

    <Group title="Workflow nodes (auto-laid-out)">
      <div className="sm:col-span-2">
        <p className="text-[11px] text-slate-500 mb-2">
          The animation engine auto-arranges nodes into rows. Set <b>Branch</b> to <b>L</b> &amp; <b>R</b> on two adjacent nodes to place them side-by-side. The last enabled node is treated as the finish state.
        </p>
        <ArrayEditor items={data.nodes} onChange={(v) => onChange(set(data, "nodes", v))}
          newItem={() => ({
            id: `node-${Date.now().toString(36)}`,
            enabled: true,
            order: (Array.isArray(data.nodes) ? data.nodes.length : 0) + 1,
            icon: "Sparkles",
            category: "Step",
            title: "New node",
            status: "Status line",
            tone: "ai",
            branch: "C",
            tip: "",
          })} label="Node" testid="flow-nodes-arr"
          renderItem={(it, ch) => (
            <div className="space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <BoolField label="Enabled" value={it.enabled !== false} onChange={(v) => ch({ ...it, enabled: v })} />
                <div className="flex-1 min-w-[120px]">
                  <NumberField label="Sort order" value={it.order} onChange={(v) => ch({ ...it, order: v })} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <IconPickerField label="Icon" value={it.icon} onChange={(v) => ch({ ...it, icon: v })} />
                <SelectField label="Accent tone" value={it.tone} onChange={(v) => ch({ ...it, tone: v })} options={FLOW_TONES} />
                <TextField label="Category (eyebrow)" value={it.category} onChange={(v) => ch({ ...it, category: v })} />
                <SelectField label="Branch position" value={it.branch || "C"} onChange={(v) => ch({ ...it, branch: v })} options={FLOW_BRANCHES} />
                <TextField label="Node title" value={it.title} onChange={(v) => ch({ ...it, title: v })} />
                <TextField label="Status line (sub-title)" value={it.status} onChange={(v) => ch({ ...it, status: v })} />
                <div className="sm:col-span-2">
                  <TextareaField label="Tooltip (on hover)" value={it.tip} onChange={(v) => ch({ ...it, tip: v })} rows={2} />
                </div>
              </div>
            </div>
          )} />
      </div>
    </Group>

    <Group title="Completion chip (after the flow runs)">
      <TextField label="Completion title" value={data.completion_title} onChange={(v) => onChange(set(data, "completion_title", v))} />
      <TextField label="Completion subtitle" value={data.completion_subtitle} onChange={(v) => onChange(set(data, "completion_subtitle", v))} />
    </Group>
  </div>
);

// ---- Campaign Analytics (Bulk section) ----
const CampaignAnalyticsForm = ({ data, onChange }) => {
  const campaign = data.campaign || {};
  const counters = data.counters || {};
  const deltas = data.kpi_deltas || {};
  const left = data.feature_left || {};
  const right = data.feature_right || {};

  return (
    <div className="space-y-4">
      <Group title="Section heading">
        <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => onChange(set(data, "eyebrow", v))} />
        <TextField label="Heading" value={data.heading} onChange={(v) => onChange(set(data, "heading", v))} />
        <div className="sm:col-span-2"><TextareaField label="Description" value={data.description} onChange={(v) => onChange(set(data, "description", v))} rows={3} /></div>
      </Group>

      <Group title="Campaign details (shown in the console header)">
        <TextField label="Campaign name" value={campaign.name} onChange={(v) => onChange(setIn(data, ["campaign", "name"], v))} />
        <TextField label="Campaign ID" value={campaign.id} onChange={(v) => onChange(setIn(data, ["campaign", "id"], v))} />
        <TextField label="Template name" value={campaign.template} onChange={(v) => onChange(setIn(data, ["campaign", "template"], v))} />
        <TextField label="Country (e.g. +91 India)" value={campaign.country} onChange={(v) => onChange(setIn(data, ["campaign", "country"], v))} />
        <TextField label="Campaign type (Utility / Marketing)" value={campaign.type} onChange={(v) => onChange(setIn(data, ["campaign", "type"], v))} />
        <NumberField label="Audience size" value={campaign.audience} onChange={(v) => onChange(setIn(data, ["campaign", "audience"], v))} />
        <TextField label="Cost (display, e.g. ₹2,750)" value={campaign.cost} onChange={(v) => onChange(setIn(data, ["campaign", "cost"], v))} />
        <NumberField label="ETA (seconds)" value={campaign.eta_seconds} onChange={(v) => onChange(setIn(data, ["campaign", "eta_seconds"], v))} />
        <NumberField label="Baseline progress (%)" value={campaign.baseline_progress} onChange={(v) => onChange(setIn(data, ["campaign", "baseline_progress"], v))} />
      </Group>

      <Group title="Realtime funnel — baseline counters (animation continues automatically)">
        <NumberField label="Delivered" value={counters.delivered} onChange={(v) => onChange(setIn(data, ["counters", "delivered"], v))} />
        <NumberField label="Read" value={counters.read} onChange={(v) => onChange(setIn(data, ["counters", "read"], v))} />
        <NumberField label="Replied" value={counters.replied} onChange={(v) => onChange(setIn(data, ["counters", "replied"], v))} />
        <NumberField label="CTA Clicked" value={counters.clicked} onChange={(v) => onChange(setIn(data, ["counters", "clicked"], v))} />
        <NumberField label="Orders" value={counters.ordered} onChange={(v) => onChange(setIn(data, ["counters", "ordered"], v))} />
        <NumberField label="Revenue (₹, e.g. 845000 = ₹8.45L)" value={counters.revenue} onChange={(v) => onChange(setIn(data, ["counters", "revenue"], v))} />
      </Group>

      <Group title="KPI card deltas (small labels under the percentage)">
        <TextField label="Deliverability delta" value={deltas.deliverability_delta} onChange={(v) => onChange(setIn(data, ["kpi_deltas", "deliverability_delta"], v))} />
        <TextField label="Read rate delta" value={deltas.read_rate_delta} onChange={(v) => onChange(setIn(data, ["kpi_deltas", "read_rate_delta"], v))} />
        <TextField label="Click rate delta" value={deltas.click_rate_delta} onChange={(v) => onChange(setIn(data, ["kpi_deltas", "click_rate_delta"], v))} />
        <TextField label="ROI subtext" value={deltas.roi_label} onChange={(v) => onChange(setIn(data, ["kpi_deltas", "roi_label"], v))} />
      </Group>

      <Group title="Outgoing WhatsApp message (shown in the floating bubble)">
        <div className="sm:col-span-2">
          <TextareaField label="Campaign message" value={data.campaign_message} onChange={(v) => onChange(set(data, "campaign_message", v))} rows={3} />
        </div>
      </Group>

      <Group title="Live activity feed — customer pool">
        <div className="sm:col-span-2">
          <p className="text-[11px] text-slate-500 mb-2">
            The activity feed, funnel cascades and WhatsApp bubble rotate through these customers automatically.
          </p>
          <ArrayEditor items={data.customers} onChange={(v) => onChange(set(data, "customers", v))}
            newItem={{ name: "New Customer", city: "City", color: "from-blue-500 to-indigo-500" }}
            label="Customer" testid="ca-customers-arr"
            renderItem={(it, ch) => (
              <div className="grid sm:grid-cols-3 gap-2">
                <TextField label="Name" value={it.name} onChange={(v) => ch({ ...it, name: v })} />
                <TextField label="City" value={it.city} onChange={(v) => ch({ ...it, city: v })} />
                <TextField label="Avatar gradient (Tailwind)" value={it.color} onChange={(v) => ch({ ...it, color: v })} />
              </div>
            )} />
        </div>
      </Group>

      <Group title="Reply snippets (random reply text)">
        <div className="sm:col-span-2">
          <ArrayEditor items={data.reply_snippets} onChange={(v) => onChange(set(data, "reply_snippets", v))}
            newItem={() => ""} label="Reply" testid="ca-replies-arr"
            renderItem={(it, ch) => <Input value={it} onChange={(e) => ch(e.target.value)} placeholder="e.g. When will my order arrive?" />} />
        </div>
      </Group>

      <Group title="CTA labels (random tap targets)">
        <div className="sm:col-span-2">
          <ArrayEditor items={data.cta_labels} onChange={(v) => onChange(set(data, "cta_labels", v))}
            newItem={() => ""} label="CTA" testid="ca-ctas-arr"
            renderItem={(it, ch) => <Input value={it} onChange={(e) => ch(e.target.value)} placeholder="e.g. Shop Now" />} />
        </div>
      </Group>

      <Group title="Right-side · Feature card 1 (with chips)">
        <IconPickerField label="Icon" value={left.icon} onChange={(v) => onChange(setIn(data, ["feature_left", "icon"], v))} />
        <TextField label="Title" value={left.title} onChange={(v) => onChange(setIn(data, ["feature_left", "title"], v))} />
        <div className="sm:col-span-2"><TextareaField label="Description" value={left.desc} onChange={(v) => onChange(setIn(data, ["feature_left", "desc"], v))} rows={2} /></div>
        <div className="sm:col-span-2">
          <ArrayEditor items={left.chips} onChange={(v) => onChange(setIn(data, ["feature_left", "chips"], v))}
            newItem={() => ""} label="Chip" testid="ca-leftchips-arr"
            renderItem={(it, ch) => <Input value={it} onChange={(e) => ch(e.target.value)} placeholder="e.g. Geography" />} />
        </div>
      </Group>

      <Group title="Right-side · Feature card 2 (with stat grid)">
        <IconPickerField label="Icon" value={right.icon} onChange={(v) => onChange(setIn(data, ["feature_right", "icon"], v))} />
        <TextField label="Title" value={right.title} onChange={(v) => onChange(setIn(data, ["feature_right", "title"], v))} />
        <div className="sm:col-span-2"><TextareaField label="Description" value={right.desc} onChange={(v) => onChange(setIn(data, ["feature_right", "desc"], v))} rows={2} /></div>
        <div className="sm:col-span-2">
          <ArrayEditor items={right.stats} onChange={(v) => onChange(setIn(data, ["feature_right", "stats"], v))}
            newItem={{ v: "100%", l: "Label" }} label="Stat" testid="ca-rightstats-arr"
            renderItem={(it, ch) => (
              <div className="grid sm:grid-cols-2 gap-2">
                <TextField label="Value" value={it.v} onChange={(v) => ch({ ...it, v })} />
                <TextField label="Label" value={it.l} onChange={(v) => ch({ ...it, l: v })} />
              </div>
            )} />
        </div>
      </Group>
    </div>
  );
};

// ---- Legal Pages (Privacy, Terms, future legal docs) ----
const CALLOUT_TONES = [
  { value: "emerald", label: "Emerald" },
  { value: "blue",    label: "Blue" },
  { value: "amber",   label: "Amber" },
  { value: "rose",    label: "Rose" },
  { value: "violet",  label: "Violet" },
  { value: "sky",     label: "Sky" },
  { value: "slate",   label: "Slate" },
];

const LegalPagesForm = ({ data, onChange }) => (
  <div className="space-y-4">
    <Group title="Legal pages">
      <div className="sm:col-span-2">
        <p className="text-[11px] text-slate-500 mb-2">
          Each entry below becomes a public page at <code>/{"{slug}"}</code> (e.g. <code>/privacy-policy</code>).
          Add more entries to publish additional legal documents — no code changes required.
        </p>
        <ArrayEditor
          items={data.pages}
          onChange={(v) => onChange(set(data, "pages", v))}
          newItem={() => ({
            slug: `legal-${Date.now().toString(36)}`,
            published: false,
            eyebrow: "Legal",
            title: "New legal page",
            description: "",
            last_updated: new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }),
            callouts: [],
            body_html: "<h2>Section title</h2><p>Write the content here…</p>",
            seo: { title: "", description: "", canonical: "", og_title: "", og_description: "", og_image: "", robots: "index,follow" },
          })}
          label="Page"
          testid="legal-pages-arr"
          renderItem={(it, ch) => {
            const seo = it.seo || {};
            return (
              <div className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <TextField label="Slug (URL path)" value={it.slug} onChange={(v) => ch({ ...it, slug: v })} />
                  <BoolField label="Published" value={it.published !== false} onChange={(v) => ch({ ...it, published: v })} />
                  <TextField label="Eyebrow" value={it.eyebrow} onChange={(v) => ch({ ...it, eyebrow: v })} />
                  <TextField label="Title" value={it.title} onChange={(v) => ch({ ...it, title: v })} />
                  <div className="sm:col-span-2">
                    <TextareaField label="Description (shown under the title)" value={it.description} onChange={(v) => ch({ ...it, description: v })} rows={2} />
                  </div>
                  <TextField label="Last updated (display string)" value={it.last_updated} onChange={(v) => ch({ ...it, last_updated: v })} />
                </div>

                {/* Callout boxes */}
                <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-3">
                  <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">Callout boxes (highlighted info cards)</div>
                  <ArrayEditor
                    items={it.callouts}
                    onChange={(v) => ch({ ...it, callouts: v })}
                    newItem={{ icon: "ShieldCheck", title: "Notice title", tone: "blue", body: "Notice body…" }}
                    label="Callout"
                    testid={`legal-callouts-${it.slug}`}
                    renderItem={(co, chc) => (
                      <div className="grid sm:grid-cols-2 gap-2">
                        <IconPickerField label="Icon" value={co.icon} onChange={(v) => chc({ ...co, icon: v })} />
                        <SelectField label="Tone" value={co.tone} onChange={(v) => chc({ ...co, tone: v })} options={CALLOUT_TONES} />
                        <TextField label="Title" value={co.title} onChange={(v) => chc({ ...co, title: v })} />
                        <div className="sm:col-span-2"><TextareaField label="Body" value={co.body} onChange={(v) => chc({ ...co, body: v })} rows={2} /></div>
                      </div>
                    )}
                  />
                </div>

                {/* Rich text body */}
                <RichTextField
                  label="Page body (use H2 for sections — they auto-populate the Table of Contents)"
                  value={it.body_html}
                  onChange={(v) => ch({ ...it, body_html: v })}
                  placeholder="Start writing your legal page…"
                  minHeight={420}
                  testid={`legal-body-${it.slug}`}
                />

                {/* SEO */}
                <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-3 space-y-2">
                  <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">SEO &amp; social</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <TextField label="SEO title" value={seo.title} onChange={(v) => ch({ ...it, seo: { ...seo, title: v } })} />
                    <TextField label="Canonical URL" value={seo.canonical} onChange={(v) => ch({ ...it, seo: { ...seo, canonical: v } })} />
                    <div className="sm:col-span-2"><TextareaField label="Meta description" value={seo.description} onChange={(v) => ch({ ...it, seo: { ...seo, description: v } })} rows={2} /></div>
                    <TextField label="Open Graph title" value={seo.og_title} onChange={(v) => ch({ ...it, seo: { ...seo, og_title: v } })} />
                    <TextField label="Open Graph image URL" value={seo.og_image} onChange={(v) => ch({ ...it, seo: { ...seo, og_image: v } })} />
                    <div className="sm:col-span-2"><TextareaField label="Open Graph description" value={seo.og_description} onChange={(v) => ch({ ...it, seo: { ...seo, og_description: v } })} rows={2} /></div>
                    <TextField label="Robots (e.g. index,follow)" value={seo.robots} onChange={(v) => ch({ ...it, seo: { ...seo, robots: v } })} />
                  </div>
                </div>

                <div className="flex items-center justify-between text-[11px] text-slate-500">
                  <span>Preview at <code>/{it.slug}</code> after saving.</span>
                  <a
                    href={`/${it.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white border border-slate-200 hover:border-emerald-300 hover:text-emerald-700 font-semibold transition-colors"
                    data-testid={`legal-preview-${it.slug}`}
                  >
                    Open preview ↗
                  </a>
                </div>
              </div>
            );
          }}
        />
      </div>
    </Group>
  </div>
);

// ---- Developer API (Code editor showcase) ----
const HTTP_METHODS = [
  { value: "GET",    label: "GET" },
  { value: "POST",   label: "POST" },
  { value: "PUT",    label: "PUT" },
  { value: "PATCH",  label: "PATCH" },
  { value: "DELETE", label: "DELETE" },
];

const DeveloperApiForm = ({ data, onChange }) => {
  const cta = data.cta || {};
  const settings = data.settings || {};
  return (
    <div className="space-y-4">
      {/* ─────────── 1. GENERAL ─────────── */}
      <Group title="General — Title, subtitle, description">
        <TextField label="Eyebrow"  value={data.eyebrow}  onChange={(v) => onChange(set(data, "eyebrow", v))}  testid="dev-api-eyebrow" placeholder="DEVELOPER API" />
        <TextField label="Heading"  value={data.heading}  onChange={(v) => onChange(set(data, "heading", v))}  testid="dev-api-heading" placeholder="Build with Wappit APIs" />
        <div className="sm:col-span-2">
          <TextField label="Subtitle (optional, shown under heading)" value={data.subtitle} onChange={(v) => onChange(set(data, "subtitle", v))} testid="dev-api-subtitle" placeholder="One-line developer hook…" />
        </div>
        <div className="sm:col-span-2">
          <TextareaField label="Description" value={data.description} onChange={(v) => onChange(set(data, "description", v))} rows={3} testid="dev-api-desc" />
        </div>
      </Group>

      {/* ─────────── 2. FEATURE BULLETS ─────────── */}
      <Group title="Feature bullets — 3 to 5 work best">
        <div className="sm:col-span-2">
          <ArrayEditor items={data.bullets} onChange={(v) => onChange(set(data, "bullets", v))}
            newItem={() => ""} label="Bullet" testid="dev-api-bullets"
            renderItem={(it, ch) => <Input value={it} onChange={(e) => ch(e.target.value)} placeholder="e.g. REST APIs" />} />
        </div>
      </Group>

      {/* ─────────── 3. CTA BUTTON ─────────── */}
      <Group title="CTA button">
        <TextField label="Button label" value={cta.label} onChange={(v) => onChange(setIn(data, ["cta", "label"], v))} placeholder="Explore API Documentation" />
        <TextField label="Button URL"   value={cta.href}  onChange={(v) => onChange(setIn(data, ["cta", "href"], v))}  placeholder="https://docs.wappit.com" />
      </Group>

      {/* ─────────── 4. ANIMATION SETTINGS ─────────── */}
      <Group title="Animation settings">
        <NumberField label="Typing speed (ms / char)"              value={settings.typing_speed_ms ?? 22}        onChange={(v) => onChange(setIn(data, ["settings", "typing_speed_ms"], v))} />
        <NumberField label="Pause after typing (ms)"               value={settings.pause_after_typing_ms ?? 600} onChange={(v) => onChange(setIn(data, ["settings", "pause_after_typing_ms"], v))} />
        <NumberField label="Status step interval (ms)"             value={settings.status_step_ms ?? 800}        onChange={(v) => onChange(setIn(data, ["settings", "status_step_ms"], v))} />
        <NumberField label="Loop pause before next example (ms)"   value={settings.loop_pause_ms ?? 1300}        onChange={(v) => onChange(setIn(data, ["settings", "loop_pause_ms"], v))} />
        <div className="sm:col-span-2">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Pre-response banner statuses</span>
          <p className="text-[11px] text-slate-500 mt-1">Shown after typing finishes, before the 200 OK badge. e.g. &quot;Request sent...&quot; → &quot;Processing...&quot;</p>
          <ArrayEditor items={settings.pre_statuses ?? ["Request sent...", "Processing..."]}
            onChange={(v) => onChange(setIn(data, ["settings", "pre_statuses"], v))}
            newItem={() => ""} label="Status" testid="dev-api-pre-statuses"
            renderItem={(s, sch) => <Input value={s} onChange={(e) => sch(e.target.value)} placeholder="e.g. Processing..." />} />
        </div>
      </Group>

      {/* ─────────── 5. API EXAMPLES (tabs) ─────────── */}
      <Group title="API examples — drag to reorder, toggle to enable">
        <div className="sm:col-span-2">
          <p className="text-[11px] text-slate-500 mb-2">
            The editor on the homepage cycles through every <strong>enabled</strong> example in order, showing each as a tab.
            Disable to hide without deleting. With zero enabled examples the section shows a clean empty state.
          </p>
          <ArrayEditor
            items={data.examples}
            onChange={(v) => onChange(set(data, "examples", v))}
            newItem={() => ({
              id: `ex-${Date.now().toString(36)}`,
              enabled: true,
              order: (data.examples?.length || 0) + 1,
              tab_label: "New API",
              method: "POST",
              endpoint: "https://api.wappit.com/v1/messages/send",
              headers: [
                { key: "Authorization", value: "Bearer ********" },
                { key: "Content-Type", value: "application/json" },
              ],
              body_json: "{\n  \"to\": \"+919876543210\"\n}",
              response_json: "{\n  \"success\": true\n}",
              status_flow: ["Queued...", "Sending...", "Delivered ✓"],
            })}
            label="Example"
            testid="dev-api-examples"
            renderItem={(it, ch) => (
              <div className="space-y-4">
                {/* Identity */}
                <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                  <span className="text-[10px] font-bold tracking-[0.18em] text-slate-500 uppercase">Identity</span>
                  <div className="grid sm:grid-cols-3 gap-2 mt-2">
                    <TextField label="Tab label" value={it.tab_label} onChange={(v) => ch({ ...it, tab_label: v })} placeholder="Send Message API" />
                    <SelectField label="HTTP method" value={it.method} onChange={(v) => ch({ ...it, method: v })} options={HTTP_METHODS} testid={`ex-method-${it.id || ""}`} />
                    <BoolField label="Enabled" value={!!it.enabled} onChange={(v) => ch({ ...it, enabled: v })} />
                  </div>
                  <div className="mt-2">
                    <TextField label="Endpoint URL" value={it.endpoint} onChange={(v) => ch({ ...it, endpoint: v })} placeholder="https://api.wappit.com/v1/messages/send" />
                  </div>
                </div>

                {/* Request — headers + body */}
                <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                  <span className="text-[10px] font-bold tracking-[0.18em] text-slate-500 uppercase">Request — headers</span>
                  <div className="mt-2">
                    <ArrayEditor items={it.headers || []} onChange={(v) => ch({ ...it, headers: v })}
                      newItem={() => ({ key: "X-Custom", value: "" })}
                      label="Header" testid={`ex-headers-${it.id || ""}`}
                      renderItem={(h, hch) => (
                        <div className="grid sm:grid-cols-2 gap-2">
                          <TextField label="Key"   value={h.key}   onChange={(v) => hch({ ...h, key: v })} />
                          <TextField label="Value" value={h.value} onChange={(v) => hch({ ...h, value: v })} />
                        </div>
                      )} />
                  </div>
                  <div className="mt-3">
                    <TextareaField label="Request body (JSON)" value={it.body_json} onChange={(v) => ch({ ...it, body_json: v })} rows={6} testid={`ex-body-${it.id || ""}`} />
                  </div>
                </div>

                {/* Response */}
                <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                  <span className="text-[10px] font-bold tracking-[0.18em] text-slate-500 uppercase">Response</span>
                  <div className="mt-2">
                    <TextareaField label="Response (JSON)" value={it.response_json} onChange={(v) => ch({ ...it, response_json: v })} rows={6} testid={`ex-response-${it.id || ""}`} />
                  </div>
                </div>

                {/* Status flow */}
                <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                  <span className="text-[10px] font-bold tracking-[0.18em] text-slate-500 uppercase">Status flow (Queued → Sending → Delivered ✓)</span>
                  <div className="mt-2">
                    <ArrayEditor items={it.status_flow || []} onChange={(v) => ch({ ...it, status_flow: v })}
                      newItem={() => ""} label="Status" testid={`ex-status-${it.id || ""}`}
                      renderItem={(s, sch) => <Input value={s} onChange={(e) => sch(e.target.value)} placeholder="e.g. Delivered ✓" />} />
                  </div>
                </div>
              </div>
            )}
          />
        </div>
      </Group>
    </div>
  );
};

// ---- Onboarding Readiness Checklist ----
const CHECKLIST_STATUSES = [
  { value: "ready", label: "Ready" },
  { value: "setup", label: "May require setup" },
  { value: "recommended", label: "Recommended" },
  { value: "optional", label: "Optional" },
];

const OnboardingChecklistForm = ({ data, onChange }) => (
  <div className="space-y-4">
    <Group title="Page heading">
      <TextField label="Eyebrow" value={data.eyebrow} onChange={(v) => onChange(set(data, "eyebrow", v))} />
      <TextField label="Review time label" value={data.review_time_label} onChange={(v) => onChange(set(data, "review_time_label", v))} />
      <div className="sm:col-span-2"><TextField label="Title" value={data.title} onChange={(v) => onChange(set(data, "title", v))} /></div>
      <div className="sm:col-span-2"><TextareaField label="Subtitle" value={data.subtitle} onChange={(v) => onChange(set(data, "subtitle", v))} rows={3} /></div>
      <TextField label="Review time value" value={data.review_time_value} onChange={(v) => onChange(set(data, "review_time_value", v))} />
    </Group>

    <Group title="Checklist sections">
      <div className="sm:col-span-2">
        <ArrayEditor items={data.sections} onChange={(v) => onChange(set(data, "sections", v))}
          newItem={() => ({ icon: "FileText", title: "New section", helper: "", items: [] })}
          label="Section" testid="checklist-sections-arr"
          renderItem={(sec, chSec) => (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-2">
                <IconPickerField label="Icon" value={sec.icon} onChange={(v) => chSec({ ...sec, icon: v })} />
                <TextField label="Title" value={sec.title} onChange={(v) => chSec({ ...sec, title: v })} />
                <div className="sm:col-span-2"><TextField label="Helper text" value={sec.helper} onChange={(v) => chSec({ ...sec, helper: v })} /></div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                <span className="text-[10px] font-bold tracking-[0.18em] text-slate-500 uppercase">Checklist items</span>
                <ArrayEditor items={sec.items} onChange={(v) => chSec({ ...sec, items: v })}
                  newItem={() => ({ label: "New item", info: "", status: "ready" })}
                  label="Item" testid={`checklist-items-${sec.title}`}
                  renderItem={(it, chIt) => (
                    <div className="grid sm:grid-cols-3 gap-2">
                      <TextField label="Label" value={it.label} onChange={(v) => chIt({ ...it, label: v })} />
                      <TextField label="Info tooltip" value={it.info} onChange={(v) => chIt({ ...it, info: v })} />
                      <SelectField label="Status" value={it.status} onChange={(v) => chIt({ ...it, status: v })} options={CHECKLIST_STATUSES} />
                    </div>
                  )} />
              </div>
            </div>
          )} />
      </div>
    </Group>

    <Group title="Timeline steps">
      <div className="sm:col-span-2">
        <ArrayEditor items={data.timeline} onChange={(v) => onChange(set(data, "timeline", v))}
          newItem={() => ({ step: "Step", title: "New step", icon: "ClipboardList", current: false })}
          label="Timeline step" testid="checklist-timeline-arr"
          renderItem={(it, ch) => (
            <div className="grid sm:grid-cols-4 gap-2">
              <TextField label="Step label" value={it.step} onChange={(v) => ch({ ...it, step: v })} />
              <TextField label="Title" value={it.title} onChange={(v) => ch({ ...it, title: v })} />
              <IconPickerField label="Icon" value={it.icon} onChange={(v) => ch({ ...it, icon: v })} />
              <BoolField label="Current?" value={!!it.current} onChange={(v) => ch({ ...it, current: v })} />
            </div>
          )} />
      </div>
    </Group>

    <Group title="Help section">
      <TextField label="Help title" value={data.help?.title} onChange={(v) => onChange(setIn(data, ["help", "title"], v))} />
      <TextField label="Help subtitle" value={data.help?.subtitle} onChange={(v) => onChange(setIn(data, ["help", "subtitle"], v))} />
      <TextField label="Talk button label" value={data.help?.talk_label} onChange={(v) => onChange(setIn(data, ["help", "talk_label"], v))} />
      <TextField label="Demo button label" value={data.help?.demo_label} onChange={(v) => onChange(setIn(data, ["help", "demo_label"], v))} />
    </Group>

    <Group title="Confirmation & proceed">
      <div className="sm:col-span-2"><TextField label="Confirm checkbox label" value={data.confirm_label} onChange={(v) => onChange(set(data, "confirm_label", v))} /></div>
      <TextField label="Proceed button label" value={data.proceed_label} onChange={(v) => onChange(set(data, "proceed_label", v))} />
      <TextField label="Proceed microcopy" value={data.proceed_micro} onChange={(v) => onChange(set(data, "proceed_micro", v))} />
      <div className="sm:col-span-2"><TextField label="Ready flash message" value={data.ready_flash} onChange={(v) => onChange(set(data, "ready_flash", v))} /></div>
    </Group>
  </div>
);

const FORM_MAP = {
  global_settings: GlobalSettingsForm,
  hero: HeroForm,
  trust_stats: TrustStatsForm,
  pricing: PricingForm,
  testimonials: TestimonialsForm,
  faq: FAQForm,
  features: FeaturesForm,
  industries: IndustriesForm,
  navigation: NavigationForm,
  footer: FooterForm,
  seo: SEOForm,
  flow_builder: FlowBuilderForm,
  campaign_analytics: CampaignAnalyticsForm,
  developer_api: DeveloperApiForm,
  legal_pages: LegalPagesForm,
  onboarding_checklist: OnboardingChecklistForm,
};

export function getFormFor(key) {
  return FORM_MAP[key] || null;
}
